import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Route, Routes } from 'react-router-dom';
import SessionManager from './components/auth/SessionManager';
import CookieConsent from './components/common/CookieConsent';
import UnifiedChatbot from './components/common/UnifiedChatbot';
import { GoogleOAuthProvider } from '@react-oauth/google';

import AboutPage from './pages/AboutPage.jsx';
import AdminDashboard from './pages/admin/AdminDashboard.jsx';
import AgentRegisterPage from './pages/auth/AgentRegisterPage.jsx';
import { AnimatePresence } from 'framer-motion';
import AnnouncementManagement from './pages/admin/AnnouncementManagement.jsx';
import AnnouncementsPage from './pages/AnnouncementsPage.jsx';
import ApiTest from './components/ApiTest.jsx';
import BillingHistoryPage from './pages/customer/BillingHistoryPage.jsx';
import BlogDetailPage from './pages/BlogDetailPage.jsx';
import BlogPage from './pages/BlogPage.jsx';
import BookInspectionPage from './pages/BookInspectionPage.jsx';
import CEAVerificationManagement from './pages/admin/CEAVerificationManagement.jsx';
import CartPage from './pages/CartPage.jsx';
import { CartProvider } from './contexts/CartContext';
import CheckoutPage from './pages/CheckoutPage.jsx';
import EventsPage from './pages/EventsPage.jsx';
import EventDetailsPage from './pages/EventDetailsPage.jsx';
import EventManagement from './pages/admin/EventManagement.jsx';
import ContactPage from './pages/ContactPage.jsx';
import CustomerDashboard from './pages/customer/CustomerDashboard.jsx';
import CustomerFeedback from './pages/customer/CustomerFeedback.jsx';
import InspectionsPage from './pages/customer/InspectionsPage.jsx';
import InspectionManagement from './pages/admin/InspectionManagement.jsx';
import ChecklistDetailPage from './pages/admin/ChecklistDetailPage.jsx';
import InspectionSchedulingPage from './pages/InspectionSchedulingPage.jsx';
import TimeSlotSelection from './pages/TimeSlotSelection.jsx';
import InspectorRoutes from './routes/InspectorRoutes.jsx';
import CustomerRegisterPage from './pages/auth/CustomerRegisterPage.jsx';
import FAQManagement from './pages/admin/FAQManagement.jsx';
import FAQPage from './pages/FAQPage.jsx';
import Footer from './components/layout/Footer.jsx';
import Header from './components/layout/Header.jsx';
import HomePage from './pages/HomePage.jsx';
import HomepageManagement from './pages/admin/HomepageManagement.jsx';
import JobDetailsPage from './pages/jobs/JobDetailsPage.jsx';
import { LanguageProvider } from './contexts/LanguageContext.js';
import LoginPage from './pages/auth/LoginPage.jsx';
import GoogleRegistrationPage from './pages/auth/GoogleRegistrationPage.jsx';
import MembershipDashboard from './components/customer/MembershipDashboard.jsx';
import MembershipPlans from './components/customer/MembershipPlans.jsx';
import MembershipSuccess from './pages/MembershipSuccess.jsx';
import { MessagesProvider } from './contexts/MessagesContext';
import NotFoundPage from './pages/NotFoundPage.jsx';
import OrderManagement from './pages/admin/OrderManagement.jsx';
import ServiceReportManagement from './pages/admin/ServiceReportManagement.jsx';
import ReviewManagement from './pages/admin/ReviewManagement.jsx';
import OrderSubmissionPage from './pages/OrderSubmissionPage.jsx';
import MessagesPage from './pages/MessagesPage.jsx';
import OrdersPage from './pages/OrdersPage.jsx';
import OrderSuccessPage from './pages/OrderSuccessPage.jsx';
import PaymentPage from './pages/PaymentPage.jsx';
import JobPaymentSuccess from './pages/JobPaymentSuccess.jsx';
import JobPaymentCancel from './pages/JobPaymentCancel.jsx';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage.jsx';
import ProfilePage from './pages/ProfilePage.jsx';
import ProtectedRoute from './components/auth/ProtectedRoute.jsx';
import RateVendor from './pages/customer/RateVendor.jsx';
import React from 'react';
import ReferralDashboardPage from './pages/ReferralDashboardPage.jsx';
import ReferralPage from './pages/ReferralPage.jsx';
import RegisterSelectionPage from './pages/auth/RegisterSelectionPage.jsx';
import ServiceDetailPage from './pages/ServiceDetailPage.jsx';
import ServicesPage from './pages/ServicesPage.jsx';
import SimpleDashboard from './pages/dashboard/SimpleDashboard.jsx';
import SubscriptionPage from './pages/SubscriptionPage.jsx';
import SubscriptionDashboard from './components/customer/SubscriptionDashboard.jsx';
import TeamChecklistPage from './pages/team/TeamChecklistPage.jsx';
import TeamSuccessPage from './pages/team/TeamSuccessPage.jsx';
import SubscriptionSuccessPage from './pages/customer/SubscriptionSuccessPage.jsx';
import SubscriptionSuccess from './pages/SubscriptionSuccess.jsx';
import UserManagement from './pages/admin/UserManagement';
import VendorDashboardPage from './pages/vendor/VendorDashboardPage.jsx';
import VendorRegisterPage from './pages/auth/VendorRegisterPage.jsx';
import ElderlyHomeCareServicesPage from './pages/ElderlyHomeCareServicesPage.jsx';
import ElderlyServiceDetailPage from './pages/ElderlyServiceDetailPage.jsx';
import ElderlyOrderSubmissionPage from './pages/ElderlyOrderSubmissionPage.jsx';
import CustomerRoutes from './routes/CustomerRoutes.jsx';
import PropertyListPage from './pages/customer/PropertyListPage.jsx';
import CustomerLayout from './components/layout/CustomerLayout.jsx';
import ElderlyCarePolicyPage from './pages/legal/ElderlyCarePolicyPage.jsx';
import EmailManagement from './pages/admin/EmailManagement.jsx';
import IndependentProviderTermsPage from './pages/legal/IndependentProviderTermsPage.jsx';
import UserTermsPage from './pages/legal/UserTermsPage.jsx';
import ReferralTermsPage from './pages/legal/ReferralTermsPage.jsx';

// Auth Pages



// Legal Pages










// Customer Components




// Membership Components




























// Context





function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || "demo-client-id"}>
      <LanguageProvider>
        <AuthProvider>
          <SessionManager />
          <MessagesProvider>
            <CartProvider>
          <AnimatePresence mode="wait">
            <Routes>
              {/* Dashboard and Admin routes (full-page layout) */}
              <Route path="/dashboard/*" element={
                <ProtectedRoute>
                  <DashboardRedirect />
                </ProtectedRoute>
              } />
              
              {/* Team routes (public access via QR codes) */}
              <Route path="/team/checklist/:qrCode" element={<TeamChecklistPage />} />
              <Route path="/team/success" element={<TeamSuccessPage />} />
              
              {/* Time slot selection for inspection scheduling */}
              <Route path="/inspection/time-slots/:requestId" element={<TimeSlotSelection />} />
              
              <Route path="/admin/announcements" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-20">
                      <AnnouncementManagement />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/homepage" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-20">
                      <HomepageManagement />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/faqs" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-20">
                      <FAQManagement />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-20">
                      <AdminDashboard />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/dashboard" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-20">
                      <AdminDashboard />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-20">
                      <UserManagement />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/orders" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-20">
                      <OrderManagement />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/service-report/:jobId" element={
                <ProtectedRoute requiredRole="admin">
                  <ServiceReportManagement />
                </ProtectedRoute>
              } />
              <Route path="/admin/cea-verification" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-24">
                      <CEAVerificationManagement />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/events" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-24">
                      <EventManagement />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/inspections" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-24">
                      <InspectionManagement />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/checklist/:checklistId" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-24">
                      <ChecklistDetailPage />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/email-management" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-24">
                      <EmailManagement />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              <Route path="/admin/reviews" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-20">
                      <ReviewManagement />
                    </main>
                  </div>
                </ProtectedRoute>
              } />
              {/* <Route path="/admin/email-test" element={
                <ProtectedRoute requiredRole="admin">
                  <div className="min-h-screen bg-gray-50">
                    <Header />
                    <main className="pt-24">
                      <EmailTestPage />
                    </main>
                  </div>
                </ProtectedRoute>
              } /> */}
              
              {/* Main layout routes (with Header and Footer) */}
              <Route path="*" element={
                <div className="min-h-screen bg-gray-50">
                  <Header />
                  <main className="pt-20">
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/services" element={<ServicesPage />} />
                      <Route path="/services/:id" element={<ServiceDetailPage />} />
                      <Route path="/blog" element={<BlogPage />} />
                      <Route path="/blog/:slug" element={<BlogDetailPage />} />
                      <Route path="/announcements" element={<AnnouncementsPage />} />
                      <Route path="/elderly-care" element={<ElderlyHomeCareServicesPage />} />
                      <Route path="/elderly-care/:id" element={<ElderlyServiceDetailPage />} />
                      <Route path="/faq" element={<FAQPage />} />
                      {/* <Route path="/pricing" element={<PricingPage />} /> */}
                      <Route path="/events" element={<EventsPage />} />
                      <Route path="/events/:id" element={<EventDetailsPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      {/* Public Inspection Scheduling from Email Token */}
                      <Route path="/schedule-inspection" element={<InspectionSchedulingPage />} />
                      {/* Public Inspection Booking from Email Link */}
                      <Route path="/book-inspection" element={<BookInspectionPage />} />
                      {/* <Route path="/email-test" element={<EmailTestPage2 />} /> */}

                      {/* Legal Pages */}
                      <Route path="/privacy" element={<PrivacyPolicyPage />} />
                      <Route path="/user-terms" element={<UserTermsPage />} />
                      <Route path="/provider-terms" element={<IndependentProviderTermsPage />} />
                      <Route path="/elderly-care-policy" element={<ElderlyCarePolicyPage />} />
                      <Route path="/referral-terms" element={<ReferralTermsPage />} />
                      
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/google-registration" element={<GoogleRegistrationPage />} />
                      <Route path="/register-selection" element={<RegisterSelectionPage />} />
                      <Route path="/customer-register" element={<CustomerRegisterPage />} />
                      <Route path="/vendor-register" element={<VendorRegisterPage />} />
                      <Route path="/agent-register" element={<AgentRegisterPage />} />
                      {/* Legacy route redirects */}
                      <Route path="/register" element={<CustomerRegisterPage />} />
                      <Route path="/order-request" element={
                        <ProtectedRoute>
                          <OrderSubmissionPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/elderly-order-request" element={
                        <ProtectedRoute>
                          <ElderlyOrderSubmissionPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/messages" element={
                        <ProtectedRoute>
                          <MessagesPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/payment/:jobId" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <PaymentPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/job-payment-success" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <JobPaymentSuccess />
                        </ProtectedRoute>
                      } />
                      <Route path="/job-payment-cancel" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <JobPaymentCancel />
                        </ProtectedRoute>
                      } />
                      <Route path="/jobs" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <CustomerFeedback />
                        </ProtectedRoute>
                      } />
                      <Route path="/properties" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <CustomerLayout>
                            <PropertyListPage />
                          </CustomerLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/jobs/:jobId" element={
                        <ProtectedRoute>
                          <JobDetailsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/checkout" element={<CheckoutPage />} />
                      <Route path="/orders" element={
                        <ProtectedRoute>
                          <OrdersPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/orders/:orderId" element={
                        <ProtectedRoute>
                          <OrderSuccessPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/orders/:orderId/success" element={
                        <ProtectedRoute>
                          <OrderSuccessPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/profile" element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/referral" element={<ReferralPage />} />
                      <Route path="/referral-dashboard" element={
                        <ProtectedRoute>
                          <ReferralDashboardPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/vendor-dashboard" element={
                        <ProtectedRoute requiredRole="vendor">
                          <VendorDashboardPage />
                        </ProtectedRoute>
                      } />
                      
                      {/* Membership Routes */}
                      <Route path="/membership/plans" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <MembershipPlans />
                        </ProtectedRoute>
                      } />
                      <Route path="/membership/dashboard" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <MembershipDashboard />
                        </ProtectedRoute>
                      } />
                      <Route path="/membership/success" element={<MembershipSuccess />} />

                      <Route path="/subscription" element={<SubscriptionPage />} />
                      {/* Public success callback for HitPay subscription flow */}
                      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                      
                      {/* Customer Dashboard Route */}
                      <Route path="/customer/dashboard" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <CustomerDashboard />
                        </ProtectedRoute>
                      } />

                      {/* Customer Feedback Route */}
                      <Route path="/customer/feedback" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <CustomerFeedback />
                        </ProtectedRoute>
                      } />

                      {/* Customer Inspections Route */}
                      <Route path="/customer/inspections" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <InspectionsPage />
                        </ProtectedRoute>
                      } />

                      {/* Legacy feedback route for backward compatibility */}
                      <Route path="/feedback" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <CustomerFeedback />
                        </ProtectedRoute>
                      } />

                      {/* Rate Vendor Route */}
                      <Route path="/rate-vendor/:jobId" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <RateVendor />
                        </ProtectedRoute>
                      } />
                      
                      {/* Customer Subscription Management Routes */}
                      <Route path="/subscription/manage" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <SubscriptionPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/subscription/dashboard" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <SubscriptionDashboard />
                        </ProtectedRoute>
                      } />

                      {/* Customer Subscription Success Route */}
                      <Route path="/customer/subscription-success" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <SubscriptionSuccessPage />
                        </ProtectedRoute>
                      } />

                      {/* Customer Billing Routes */}
                      <Route path="/subscription/billing-history" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <BillingHistoryPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/subscription/billing-history/:subscriptionId" element={
                        <ProtectedRoute requiredRole={['customer', 'referral']}>
                          <BillingHistoryPage />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/api-test" element={<ApiTest />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </main>
                  <Footer />
                </div>
              } />
            </Routes>
          </AnimatePresence>
          </CartProvider>
        </MessagesProvider>
        <CookieConsent />
        <UnifiedChatbot />
      </AuthProvider>
    </LanguageProvider>
    </GoogleOAuthProvider>
  );
}

// Dashboard redirect component to route users based on their role
const DashboardRedirect = () => {
  const { user } = useAuth();

  console.log('DashboardRedirect - User:', user);
  console.log('DashboardRedirect - User role:', user?.role);

  if (!user) {
    console.log('DashboardRedirect - No user, showing loading');
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirect referral agents to customer dashboard (they have same functions as customers)
  if (user.role === 'referral') {
    console.log('DashboardRedirect - Referral user, showing CustomerRoutes with referral features');
    return <CustomerRoutes />;
  }

  // Redirect inspectors to their specialized dashboard
  if (user.role === 'inspector') {
    console.log('DashboardRedirect - Inspector user, showing inspector dashboard');
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <InspectorRoutes />
      </div>
    );
  }

  // Redirect customers to their specialized dashboard
  if (user.role === 'customer') {
    console.log('DashboardRedirect - Customer user, showing CustomerRoutes');
    return <CustomerRoutes />;
  }

  // Redirect regular users (not admin/referral/inspector) to customer dashboard
  if (user.role === 'user' || user.role === 'member' || !user.role || (user.role !== 'admin' && user.role !== 'referral' && user.role !== 'inspector' && user.role !== 'vendor')) {
    console.log('DashboardRedirect - Regular user, showing CustomerRoutes');
    return <CustomerRoutes />;
  }

  // Default dashboard for vendors and other roles
  console.log('DashboardRedirect - Vendor/other role, showing SimpleDashboard');
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <SimpleDashboard />
    </div>
  );
};

export default App; 